import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UrlCombobox } from "./url-combobox";
import type {
  AuditableUrl,
  AuditableUrlGroups,
} from "@/services/auditable-urls";

interface PageScannerProps {
  groups: AuditableUrlGroups;
}

const buildAuditUrl = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set("wya11y_audit", "1");
    return parsed.toString();
  } catch {
    // Fall back to the old concat if URL parsing fails for any reason.
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}wya11y_audit=1`;
  }
};

export function PageScanner({ groups }: PageScannerProps) {
  const [selected, setSelected] = useState<AuditableUrl | null>(null);

  // If the URL list refreshes and the previously-chosen URL is no longer
  // present, drop the selection so the Scan button disables and the
  // trigger matches the dropdown contents.
  useEffect(() => {
    if (!selected) return;
    const stillPresent = Object.values(groups)
      .flat()
      .some((item) => item.url === selected.url);
    if (!stillPresent) {
      setSelected(null);
    }
  }, [groups, selected]);

  const handleScan = () => {
    if (!selected) return;
    window.open(buildAuditUrl(selected.url), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="cy:bg-white cy:border cy:border-[#cacee4] cy:rounded-[12px] cy:px-8 cy:py-6">
      <div className="cy:flex cy:flex-col cy:gap-8">
        <div className="cy:flex cy:flex-col cy:gap-2">
          <h2 className="cy:font-bold cy:text-[20px] cy:text-[#21242d] cy:tracking-[-0.4px]">
            Select a page to scan
          </h2>
          <p className="cy:text-[14px] cy:text-[#4e4b66] cy:tracking-[-0.28px]">
            Pick any URL that visitors can reach. Drafts, password-protected,
            and admin-only URLs are hidden.
          </p>
        </div>

        <div className="cy:flex cy:flex-wrap cy:gap-3 cy:items-center">
          <UrlCombobox
            groups={groups}
            value={selected?.url ?? ""}
            onChange={setSelected}
          />
          <Button
            type="button"
            disabled={!selected}
            onClick={handleScan}
            className="cy:bg-[#0b66e4] cy:hover:bg-[#0b66e4]/90 cy:text-white cy:rounded-[3px] cy:h-[40px] cy:px-3 cy:font-semibold cy:text-[15px]"
          >
            Scan page
            <ExternalLink className="cy:size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
